/* eslint-disable react/no-danger */
/* eslint-disable no-unused-expressions */
import React from "react"
import PropTypes from "prop-types"
import { DateTime } from "luxon"
import styled from "styled-components"
import remark from "remark"
import remarkHTML from "remark-html"
import { Flex, Box, Image, Text } from "rebass"
import { useStaticQuery, graphql } from "gatsby"

import withTranslation from "../i18n/withTranslation"
import RelatedThumb from "../components/RelatedThumb"
import Share from "../components/Share"
import SEO from "../components/Seo"
import { theme } from "../components/Layout"

function createMarkup(html) {
  return { __html: html }
}

const toHTML = value =>
  remark()
    .use(remarkHTML)
    .processSync(value)
    .toString()

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  p {
    color: ${theme.colors.info};
    line-height: 1.8;
  }

  blockquote {
    background: black;
    color: white;
    padding: 20px;
    line-height: 1.6;
    font-size: 1.3em;
    font-style: italic;
    margin: 30px 0;
  }

  img {
    max-width: 100%;
  }
`

const Title = styled.h1`
  text-align: center;
  text-transform: uppercase;
  font-weight: 700;
  margin: 40px auto;
  width: 50%;

  @media screen and (max-width: ${theme.breakpoints[1]}) {
    margin: 30px auto;
    width: 90%;
  }
`

const ImgContainer = styled.div`
  position: relative;

  img {
    width: 100%;
  }

  > div {
    position: absolute;
    bottom: 5px;
    left: 0;

    @media screen and (max-width: ${theme.breakpoints[1]}) {
      bottom: -48px;
      right: 0;
      justify-content: flex-end;
    }
  }
`

const BlogPost = ({ pageContext: { post }, i18n }) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            url
          }
        }
      }
    `
  )

  const {
    html,
    frontmatter: { related: relatedSource },
  } = post

  const { language } = i18n

  return (
    <>
      <SEO
        title={post.frontmatter.title}
        description={post.excerpt}
        image={post.frontmatter.featured}
        slug={post.fields.slug}
      />
      <Box as="article" pt={[0, 5]}>
        <Title>{post.frontmatter.title}</Title>
        <ImgContainer>
          <Image
            src={post.frontmatter.featured}
            title={post.frontmatter.title}
          />
          <Share
            simple
            socialConfig={{
              config: {
                url: `${site.siteMetadata.url}${post.fields.slug}`,
                title: post.frontmatter.title,
              },
            }}
          />
        </ImgContainer>
        <Flex
          sx={{
            flexDirection: ["column", "column", "row"],
            width: "980px",
            mt: [5, 5, 0],
            mx: "auto",
            px: [4],
            maxWidth: "100%",
          }}
        >
          <Flex sx={{ flex: ["100% 0 0", "100% 0 0", "340px 0 0"] }}>
            <Text as="p" sx={{ fontWeight: "bold", mt: 3, mb: 4 }}>
              {DateTime.fromISO(post.frontmatter.publish).toFormat(
                "MMMM dd, yyyy",
                {
                  locale: language === "es_MX" ? "es" : "en",
                }
              )}
            </Text>
          </Flex>
          <ContentWrapper>
            {language === "es_MX" ? (
              <div
                dangerouslySetInnerHTML={createMarkup(
                  toHTML(post.frontmatter.bodyES)
                )}
              />
            ) : (
              <div dangerouslySetInnerHTML={createMarkup(html)} />
            )}

            {relatedSource.title &&
              relatedSource.excerpt &&
              relatedSource.url && <RelatedThumb data={relatedSource} />}

            <Share
              socialConfig={{
                config: {
                  url: `${site.siteMetadata.url}${post.fields.slug}`,
                  title: post.frontmatter.title,
                },
              }}
            />
          </ContentWrapper>
        </Flex>
      </Box>
    </>
  )
}

BlogPost.propTypes = {
  pageContext: PropTypes.shape({
    post: PropTypes.shape({
      frontmatter: PropTypes.shape({
        publish: PropTypes.string,
        title: PropTypes.string,
        featured: PropTypes.string,
        bodyES: PropTypes.string,
        related: PropTypes.shape({
          title: PropTypes.string,
          url: PropTypes.string,
          excerpt: PropTypes.string,
          featured: PropTypes.string,
        }),
      }),
      excerpt: PropTypes.string.isRequired,
      html: PropTypes.element.isRequired,
      fields: PropTypes.shape({
        slug: PropTypes.string,
      }).isRequired,
    }),
  }),
  i18n: PropTypes.func.isRequired,
}

export default withTranslation(BlogPost)
